import { CbhIcon, Divider, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { formatHoursDurationCompact } from "@src/appV2/redesign/utils/formatHoursDurationCompact";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { getShiftClockedInDurationInHours } from "@src/appV2/redesign/utils/getShiftClockedInDuration";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";

interface ReadOnlyTimesheetTimeEntriesProps {
  displayTimesheet: TimesheetEntry;
  workplaceTimezone: string;
  formatBreakDisplay: () => string;
}

export function ReadOnlyTimesheetTimeEntries(props: ReadOnlyTimesheetTimeEntriesProps) {
  const { displayTimesheet, workplaceTimezone, formatBreakDisplay } = props;

  function calculateActualWorkedTime(timesheet: TimesheetEntry): string {
    const clockInOut = {
      start: timesheet.clockInTime.toISOString(),
      end: timesheet.clockOutTime.toISOString(),
    };

    const lunchInOut =
      timesheet.breakStartTime && timesheet.breakEndTime
        ? {
            start: timesheet.breakStartTime.toISOString(),
            end: timesheet.breakEndTime.toISOString(),
          }
        : undefined;

    const workedHours = getShiftClockedInDurationInHours(clockInOut, lunchInOut) ?? 0;
    return formatHoursDurationCompact(workedHours);
  }

  function formatWorkplaceTime(date: Date): string {
    return formatTimeWithTimezone(date, workplaceTimezone, false);
  }

  return (
    <Stack spacing={4}>
      {/* Clock In */}
      <Stack direction="row" spacing={3} alignItems="center">
        <CbhIcon size="medium" type="clock" />
        <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
          Clock in
        </Text>
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatWorkplaceTime(displayTimesheet.clockInTime)}
        </Text>
      </Stack>

      {/* Break */}
      <Stack direction="row" spacing={3} alignItems="center">
        <CbhIcon size="medium" type="break" />
        <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
          Break
        </Text>
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatBreakDisplay()}
        </Text>
      </Stack>

      {/* Clock Out */}
      <Stack direction="row" spacing={3} alignItems="center">
        <CbhIcon size="medium" type="clock" />
        <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
          Clock out
        </Text>
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatWorkplaceTime(displayTimesheet.clockOutTime)}
        </Text>
      </Stack>

      <Divider />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text bold variant="h5" sx={{ color: (theme) => theme.palette.text.tertiary }}>
          Total
        </Text>
        <Text bold variant="h5" sx={{ color: (theme) => theme.palette.success.main }}>
          {calculateActualWorkedTime(displayTimesheet)}
        </Text>
      </Stack>
    </Stack>
  );
}
